import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  clearSpotifyAuthScratchKeys,
  emitSpotifyAuthChanged,
  SPOTIFY_AUTH_STATE_KEY,
  SPOTIFY_AUTH_VERIFIER_KEY,
} from "../services/spotifyAuth";
import { SpotifyService } from "../services/spotifyService";

export default function SpotifyOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>("Processing...");
  const [error, setError] = useState<string | null>(null);

  const spotifyService = useMemo(() => new SpotifyService(), []);

  const redirectUri = useMemo(
    () =>
      process.env.REACT_APP_SPOTIFY_REDIRECT_URI ||
      `${window.location.origin}/oauth/spotify-callback`,
    [],
  );

  useEffect(() => {
    const handleCallback = async () => {
      const authCode = searchParams.get("code");
      const returnedState = searchParams.get("state");
      const oauthError = searchParams.get("error");

      if (oauthError) {
        setError(`OAuth error: ${oauthError}`);
        setTimeout(() => navigate("/admin"), 3000);
        return;
      }

      if (!authCode) {
        setError("No authorization code received");
        setTimeout(() => navigate("/admin"), 3000);
        return;
      }

      const expectedState = localStorage.getItem(SPOTIFY_AUTH_STATE_KEY);
      const verifier = localStorage.getItem(SPOTIFY_AUTH_VERIFIER_KEY);

      if (!expectedState || !verifier || returnedState !== expectedState) {
        setError("Spotify login validation failed");
        setTimeout(() => navigate("/admin"), 3000);
        return;
      }

      try {
        setStatus("Exchanging authorization code...");

        await spotifyService.exchangeSpotifyAuthCode({
          code: authCode,
          clientId: process.env.REACT_APP_SPOTIFY_CLIENT_ID,
          redirectUri,
          codeVerifier: verifier,
        });

        clearSpotifyAuthScratchKeys();
        emitSpotifyAuthChanged();

        setStatus("Connected successfully! Redirecting...");
        setTimeout(() => navigate("/admin"), 1500);
      } catch (e: any) {
        setError(e?.message ?? "Failed to complete authentication");
        setTimeout(() => navigate("/admin"), 3000);
      }
    };

    handleCallback();
  }, [navigate, redirectUri, searchParams, spotifyService]);

  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h2>Spotify Authentication</h2>
      {error ? (
        <div style={{ marginTop: "1rem", color: "crimson" }}>{error}</div>
      ) : (
        <div style={{ marginTop: "1rem" }}>{status}</div>
      )}
    </div>
  );
}
